import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Shield, Users } from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const MAIN_APP_USER_MANAGEMENT_URL = 'https://teijin.factory-pilot.ai/admin/users';

const UserManagement = () => {
  const { t } = useTranslation();
  const { currentTenantId } = useAuth();

  const userManagementUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return MAIN_APP_USER_MANAGEMENT_URL;
    }

    const url = new URL(MAIN_APP_USER_MANAGEMENT_URL);
    url.searchParams.set('returnTo', '/admin/users');
    if (currentTenantId) {
      url.searchParams.set('tenantId', currentTenantId);
    }
    return url.toString();
  }, [currentTenantId]);

  const openUserManagement = () => {
    window.location.href = userManagementUrl;
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        backTo="/"
        backLabel={t('manager.backToDashboard')}
        title={t('userManagement.title')}
        subtitle={t('userManagement.redirect.subtitle', 'Open centralized user and role administration in Factory Pilot')}
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>{t('userManagement.redirect.title', 'Manage Access In Factory Pilot')}</CardTitle>
                  <CardDescription>
                    {t(
                      'userManagement.redirect.description',
                      'User roles and permissions for Teijin are now managed in the main Factory Pilot application.',
                    )}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  'userManagement.redirect.body',
                  'Open the centralized administration screen to review memberships, assign roles, and manage permissions.',
                )}
              </p>

              <div className="flex flex-wrap gap-3">
                <Button onClick={openUserManagement} size="lg">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('userManagement.redirect.open', 'Open User Management')}
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={MAIN_APP_USER_MANAGEMENT_URL} target="_blank" rel="noreferrer">
                    <Users className="mr-2 h-4 w-4" />
                    {t('userManagement.redirect.openNewTab', 'Open In New Tab')}
                  </a>
                </Button>
              </div>

              <p className="text-xs text-muted-foreground break-all">
                {userManagementUrl}
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UserManagement;
